interface ICacheEntry {
  value: unknown,
  expiresAt: number,
}

export interface IQueryCacheSnapshotEntry {
  key: string,
  value: unknown,
  expiresAt: number,
  subscribers: number,
}

const DEFAULT_MAX_ENTRIES = 10;

const cache = new Map<string, ICacheEntry>();
const inflight = new Map<string, Promise<unknown>>();
const listeners = new Map<string, Set<() => void>>();

export function getQueryCacheSnapshot(): IQueryCacheSnapshotEntry[] {
  return Array.from(cache, ([key, { value, expiresAt }]) => ({
    key,
    value,
    expiresAt,
    subscribers: listeners.get(key)?.size ?? 0,
  }));
}

function pruneExpired(): void {
  const now = Date.now();

  cache.forEach(({ expiresAt }, key) => {
    if (expiresAt <= now) {
      cache.delete(key);
    }
  });
}

export function getCachedValue<T>(key: string): T | undefined {
  pruneExpired();

  return cache.get(key)?.value as T | undefined;
}

export function setCachedValue<T>(key: string, value: T, ttlMs: number): void {
  pruneExpired();
  cache.delete(key);
  cache.set(key, { value, expiresAt: Date.now() + ttlMs });

  while (cache.size > DEFAULT_MAX_ENTRIES) {
    cache.delete(cache.keys().next().value);
  }
}

// Concurrent callers on the same key share one request; its result is cached only
// if the key wasn't invalidated while it was in flight.
export function fetchQuery<T>(key: string, fetch: () => Promise<T>, ttlMs: number): Promise<T> {
  const pending = inflight.get(key) as Promise<T> | undefined;

  if (pending) {
    return pending;
  }

  const request: Promise<T> = fetch()
    .then((value) => {
      if (inflight.get(key) === request) {
        setCachedValue(key, value, ttlMs);
      }

      return value;
    })
    .finally(() => {
      if (inflight.get(key) === request) {
        inflight.delete(key);
      }
    });

  inflight.set(key, request);

  return request;
}

export function subscribe(key: string, callback: () => void): () => void {
  let callbacks = listeners.get(key);

  if (!callbacks) {
    callbacks = new Set();
    listeners.set(key, callbacks);
  }

  callbacks.add(callback);

  return () => {
    callbacks.delete(callback);

    if (callbacks.size === 0 && listeners.get(key) === callbacks) {
      listeners.delete(key);
    }
  };
}

// Whole segments only: 'a.bond' matches 'a.bond.bwic' and 'a.bond:{...}', not 'a.bonds'.
function matchesPrefix(key: string, prefix: string): boolean {
  if (!key.startsWith(prefix)) {
    return false;
  }

  const next = key.charAt(prefix.length);

  return next === '' || next === ':' || next === '.' || prefix.endsWith(':') || prefix.endsWith('.');
}

export function invalidateQueryKey(key: string): void {
  cache.delete(key);
  inflight.delete(key);
  listeners.get(key)?.forEach((callback) => callback());
}

export function invalidateQuery(keyOrPrefix: string): void {
  // A query can be listening or in flight with no cache entry (evicted or expired).
  const keys = new Set([...cache.keys(), ...inflight.keys(), ...listeners.keys()]);

  keys.forEach((key) => {
    if (matchesPrefix(key, keyOrPrefix)) {
      invalidateQueryKey(key);
    }
  });
}
